'use client';

import {
  UPZI_LOGIN_SUCCESS_EVENT_NAME,
  UPZI_SESSION_EXPIRED_EVENT_NAME,
} from '@/common';
import {
  getSessionExpiredCloseRedirect,
  getSessionExpiredReturnTo,
  shouldOpenSessionExpiredLogin,
} from '@/config/auth-routes';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

const AuthModal = dynamic(() => import('.'), { ssr: false });

export default function SessionExpiredLoginModal() {
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const pathname = usePathname();
  const router = useRouter();
  const closeRedirectTo = getSessionExpiredCloseRedirect(pathname);

  const handleSessionExpired = useCallback(() => {
    const { hash, pathname, search } = window.location;

    if (!shouldOpenSessionExpiredLogin(pathname, search)) {
      return;
    }

    setReturnTo(getSessionExpiredReturnTo(pathname, search, hash));
  }, []);

  const handleLoginSuccess = useCallback(() => setReturnTo(null), []);

  const handleClose = useCallback(() => {
    setReturnTo(null);
    // Sync server-rendered session UI without navigating away from public content.
    if (!closeRedirectTo) router.refresh();
  }, [closeRedirectTo, router]);

  useEffect(() => {
    window.addEventListener(
      UPZI_SESSION_EXPIRED_EVENT_NAME,
      handleSessionExpired
    );
    window.addEventListener(UPZI_LOGIN_SUCCESS_EVENT_NAME, handleLoginSuccess);

    return () => {
      window.removeEventListener(
        UPZI_SESSION_EXPIRED_EVENT_NAME,
        handleSessionExpired
      );
      window.removeEventListener(
        UPZI_LOGIN_SUCCESS_EVENT_NAME,
        handleLoginSuccess
      );
    };
  }, [handleLoginSuccess, handleSessionExpired]);

  if (!returnTo) return null;

  return (
    <AuthModal
      isLoggedIn={false}
      defaultOpen
      redirectTo={returnTo}
      closeRedirectTo={closeRedirectTo}
      onClose={handleClose}
    />
  );
}
